import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Grid
} from '@mui/material';
import {
  Save as SaveIcon,
  BugReport as BugReportIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../config/api';

const DebugLogsTab = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState({
    enabled: true,
    visible_log_types: []
  });
  const [logTypes, setLogTypes] = useState([]);

  useEffect(() => {
    fetchLogTypes();
    fetchSettings();
  }, []);

  const fetchLogTypes = async () => {
    try {
      const response = await api.get('/api/admin/debug-logs/log-types');
      setLogTypes(response.data.logTypes);
    } catch (err) {
      console.error('Error fetching log types:', err);
      setError('Failed to load log types');
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Use company_id (snake_case) as returned by backend API
      const companyId = user.company_id || user.companyId;
      
      if (!companyId) {
        setError('Company ID not found. Please log out and log back in.');
        setLoading(false);
        return;
      }
      
      const response = await api.get(`/api/admin/debug-logs/settings/${companyId}`);
      setSettings(response.data.settings);
      
    } catch (err) {
      console.error('Error fetching debug log settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = (event) => {
    setSettings({
      ...settings,
      enabled: event.target.checked
    });
  };

  const handleToggleLogType = (logType) => {
    const currentTypes = settings.visible_log_types || [];
    const newTypes = currentTypes.includes(logType)
      ? currentTypes.filter(t => t !== logType)
      : [...currentTypes, logType];
    
    setSettings({
      ...settings,
      visible_log_types: newTypes
    });
  };

  const handleSelectAll = () => {
    setSettings({
      ...settings,
      visible_log_types: logTypes.map(lt => lt.value)
    });
  };

  const handleDeselectAll = () => {
    setSettings({
      ...settings,
      visible_log_types: []
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Use company_id (snake_case) as returned by backend API
      const companyId = user.company_id || user.companyId;
      
      if (!companyId) {
        setError('Company ID not found. Please log out and log back in.');
        setSaving(false);
        return;
      }
      
      await api.put(`/api/admin/debug-logs/settings/${companyId}`, settings);
      
      setSuccess('Debug log settings saved successfully');
      
    } catch (err) {
      console.error('Error saving debug log settings:', err);
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BugReportIcon /> Debug Logs Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure which log types are visible for this company. Logs will still be generated but hidden from the UI based on these settings.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={settings.enabled}
                onChange={handleToggleEnabled}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Debug Logs Enabled
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {settings.enabled 
                    ? 'Users can view debug logs via the bug icon in chat' 
                    : 'Debug logs are completely hidden from users'}
                </Typography>
              </Box>
            }
          />
        </CardContent>
      </Card>

      {settings.enabled && (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Visible Log Types ({settings.visible_log_types?.length || 0}/{logTypes.length})
            </Typography>
            <Box>
              <Button size="small" onClick={handleSelectAll} sx={{ mr: 1 }}>
                Select All
              </Button>
              <Button size="small" onClick={handleDeselectAll}>
                Deselect All
              </Button>
            </Box>
          </Box>

          <Card>
            <CardContent>
              <FormGroup>
                <Grid container spacing={2}>
                  {logTypes.map((logType) => {
                    const isChecked = settings.visible_log_types?.includes(logType.value);
                    return (
                      <Grid item xs={12} sm={6} md={4} key={logType.value}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isChecked}
                              onChange={() => handleToggleLogType(logType.value)}
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {logType.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {logType.description}
                              </Typography>
                            </Box>
                          }
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </FormGroup>
            </CardContent>
          </Card>
        </>
      )}

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            borderRadius: 2,
            background: 'linear-gradient(45deg, var(--color-primary) 30%, var(--color-accent) 90%)',
          }}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Note:</strong> Changes take effect immediately. All logs are still being generated and stored in the database - this only controls what users can see in the UI.
        </Typography>
      </Alert>
    </Box>
  );
};

export default DebugLogsTab;
